import React, { useEffect, useState } from 'react';
import { Bell, Loader2, Shield } from 'lucide-react';
import { authApi } from 'api/auth';
import { ToastService } from '@/services/toast.service';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Setări de cont — UI aliniat la restul portalului (claude-design).
 * Momentan: notificare email la autentificare de pe un dispozitiv nou.
 */
const AccountSettings: React.FC = () => {
  const { currentLanguage: lang } = useLanguage();
  const isRo = lang === 'ro';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifyOnNewLogin, setNotifyOnNewLogin] = useState(false);

  useEffect(() => {
    document.body.classList.add('dashboard-claude-bg');
    return () => {
      document.body.classList.remove('dashboard-claude-bg');
    };
  }, []);

  useEffect(() => {
    authApi
      .getPreferences()
      .then((data) => setNotifyOnNewLogin(data?.notifyOnNewLogin ?? true))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const next = !notifyOnNewLogin;
    setNotifyOnNewLogin(next);
    setSaving(true);
    try {
      await authApi.updatePreferences({ notifyOnNewLogin: next });
      ToastService.success?.(
        isRo ? 'Preferințele au fost salvate' : 'Preferences saved',
      );
    } catch {
      setNotifyOnNewLogin(!next);
      ToastService.error?.(
        isRo
          ? 'Nu am putut salva preferința. Încearcă din nou.'
          : 'Could not save preference. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ color: 'var(--text)', maxWidth: 640 }}>
      <div style={{ marginBottom: 18 }}>
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          {isRo ? 'Setări cont' : 'Account settings'}
        </h1>
        <div className="muted fs-13 mt-1">
          {isRo
            ? 'Gestionează preferințele de securitate și notificare.'
            : 'Manage security and notification preferences.'}
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="card-header">
          <div>
            <div className="title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Shield size={16} strokeWidth={2} style={{ color: 'var(--accent)' }} />
              {isRo ? 'Securitate & notificări' : 'Security & notifications'}
            </div>
            <div className="sub">
              {isRo
                ? 'Alerte legate de accesul la cont'
                : 'Alerts related to account access'}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div className="row gap-3" style={{ alignItems: 'flex-start', minWidth: 0, flex: 1 }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Bell size={17} strokeWidth={1.9} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="fw-600 fs-14">
                {isRo
                  ? 'Notificare la autentificare nouă'
                  : 'New login notification'}
              </div>
              <div className="muted fs-12 mt-1" style={{ lineHeight: 1.45 }}>
                {isRo
                  ? 'Primești un email când contul tău este accesat de pe un dispozitiv nou.'
                  : 'Receive an email when your account is accessed from a new device.'}
              </div>
              {loading && (
                <div className="row gap-2 muted fs-12 mt-2" style={{ alignItems: 'center' }}>
                  <Loader2 size={13} className="ai-spin" />
                  {isRo ? 'Se încarcă…' : 'Loading…'}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            role="switch"
            aria-checked={notifyOnNewLogin}
            disabled={loading || saving}
            onClick={toggle}
            title={
              notifyOnNewLogin
                ? isRo
                  ? 'Dezactivează'
                  : 'Disable'
                : isRo
                  ? 'Activează'
                  : 'Enable'
            }
            style={{
              position: 'relative',
              width: 44,
              height: 24,
              borderRadius: 99,
              border: 'none',
              cursor: loading || saving ? 'not-allowed' : 'pointer',
              flexShrink: 0,
              opacity: loading || saving ? 0.55 : 1,
              background: notifyOnNewLogin ? 'var(--accent)' : 'var(--surface-3)',
              transition: 'background 0.15s',
              padding: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: notifyOnNewLogin ? 22 : 2,
                width: 20,
                height: 20,
                borderRadius: 99,
                background: notifyOnNewLogin ? 'var(--accent-fg)' : 'var(--surface)',
                boxShadow: 'var(--shadow-sm)',
                transition: 'left 0.15s',
              }}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
